import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { environment } from '@app/env';
// error handler
import { handleError } from '@app/helpers/api-error-handler';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
// models
import { Auth, LoginModel } from '../_models/auth.model';
import { Accounts } from '../_models/forgot-password.model';
import { ActivatedRoute } from '@angular/router';
import { TrackingService } from './tracking.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private trackingService = inject(TrackingService);

  private url = environment.api_url;

  login(payload: LoginModel): Observable<Auth> {
    return this.http.post<Auth>(`${this.url}/Administration/account/token`, payload).pipe(
      tap(response => {
        if (isPlatformBrowser(this.platformId)) {
          this.toLocalStorage(response);
          this.trackingService.trackEvent('login', { method: 'EMP' });
        }
      })
    );
  }

  validateCredentials(params: { username: string; password: string }): Observable<unknown> {
    const body = new URLSearchParams();
    body.set('cmd', 'login');
    body.set('username', params.username);
    body.set('password', params.password);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post(environment.validation, body.toString(), { headers }).pipe(catchError(handleError));
  }

  public changeAccount(companyID: number): Observable<never> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get<never>(`${environment.validation}?cmd=ChangeAccount&Account=${companyID}`, {
      headers: headers
    });
  }

  public getAccounts(auth_token: string): Observable<Accounts[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth_token}`
    });

    return this.http.get<Accounts[]>(`${environment.api_url}/Administration/Account/Accounts`, { headers: headers });
  }

  public getMenu() {
    return this.http.get<Accounts[]>(`${environment.api_emp}/menu`);
  }

  resetPassword(token: string, body: { email: string }): Observable<never> {
    return this.http.post<never>(`${this.url}Reset/Admin/${token}`, body).pipe(catchError(handleError));
  }

  forgotPassword(email: string): Observable<never> {
    return this.http.post<never>(`${this.url}Recover`, { email }).pipe(catchError(handleError));
  }

  private toLocalStorage(data: Auth): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('authToken', data.auth_token);
      localStorage.setItem('userName', data.username);
    }
  }

  isAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const cookieId = 'EnergyMarketPrice';
      const cookies = this.document.cookie;
      return cookies.includes(cookieId);
    }
    return false;
  }

  public redirectAfterLogin(): void {
    let redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl');
    if (redirectUrl) {
      if (redirectUrl.includes('www.energymarketprice.com')) {
        location.href = redirectUrl;
      } else {
        redirectUrl = redirectUrl.charAt(0) === '/' ? redirectUrl : `/${redirectUrl}`;
        location.href = `https://www.energymarketprice.com${redirectUrl}`;
      }
    } else {
      const clientreport = this.route.snapshot.queryParamMap.get('clientreport');
      const coreenergy = this.route.snapshot.queryParamMap.get('coreenergy');
      switch (true) {
        case clientreport != null:
          location.href = `https://www.energymarketprice.com/weekly/client/`;
          break;
        case coreenergy != null:
          location.href = `https://www.energymarketprice.com/coreenergy/client/`;
          break;
        default:
          location.href = `https://www.energymarketprice.com/new/SitePage.asp?act=accountdata`;
      }
    }
  }
}
